import * as formik from 'formik';
import moment from 'moment';
import * as fabric from 'office-ui-fabric-react';
import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import * as api from '../../../../../api/generated';
import { KendoLoading } from '../../../../../components';
import { IWindowComponentProps } from '../../../../../components/windows/WindowContext';
import { servicePurposeType, servicesDisplayInTerminalType, ticketsNumerationType } from '../../../../../enums';
import { IServiceListItem } from '../../../../../models/classes/IService';

interface IServiceEditModalProps {
    serviceId: number;
    organizationInformationId: number;
    refetchServices(): Promise<void>;
}

const validationSchema = Yup.object<IServiceListItem>().shape({
    name_LT: Yup.string().required('Šis laukas yra privalomas').max(50, 'Maksimalus lauko ilgis: 50 simbolių'),
    name_EN: Yup.string().required('Šis laukas yra privalomas').max(50, 'Maksimalus lauko ilgis: 50 simbolių'),
    name_RU: Yup.string().required('Šis laukas yra privalomas').max(50, 'Maksimalus lauko ilgis: 50 simbolių'),
    description_LT: Yup.string().required('Šis laukas yra privalomas').max(200, 'Maksimalus lauko ilgis: 200 simbolių'),
    description_EN: Yup.string().required('Šis laukas yra privalomas').max(200, 'Maksimalus lauko ilgis: 200 simbolių'),
    description_RU: Yup.string().required('Šis laukas yra privalomas').max(200, 'Maksimalus lauko ilgis: 200 simbolių'),

    ticketNumberPrefix: Yup.string().when('ticketNumerationType', {
        is: ticketsNumerationType.ServiceNumeration,
        then: Yup.string().required('Šis laukas yra privalomas').max(2, 'Maksimalus dydis - 2 simboliai')
    }),
    ticketNumberRangeFrom: Yup.number().when('ticketNumerationType', {
        is: ticketsNumerationType.ServiceNumeration,
        then: Yup.number()
            .required('Šis laukas yra privalomas')
            .positive('Skaičius privalo būti teigiamas')
            .lessThan(Yup.ref('ticketNumberRangeTo'), 'Reikšmė turi būti mažesnė už intervalo pabaigą')
    }),
    ticketNumberRangeTo: Yup.number().when('ticketNumerationType', {
        is: ticketsNumerationType.ServiceNumeration,
        then: Yup.number()
            .required('Šis laukas yra privalomas')
            .positive('Skaičius turi būti teigiamas')
            .moreThan(Yup.ref('ticketNumberRangeFrom'), 'Reikšmė turi būti didesnė už intervalo pradžią')
            .lessThan(1000, 'Reikšmė privalo būti mažesnė už 1000')
    })
});

const ServiceEditModalWindow: FC<IWindowComponentProps<IServiceEditModalProps>> = (props) => {
    const {
        componentProps: { serviceId, organizationInformationId, refetchServices },
        onClose
    } = props;

    const [isLoading, setIsLoading] = useState(false);
    const [service, setService] = useState<IServiceListItem | null>(null);
    const [ticketTemplateOptions, setTicketTemplateOptions] = useState<fabric.IDropdownOption[] | null>(null);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const [serviceResp, ticketTemplateOptionsResponse] = await Promise.all([
                api.service.getService(serviceId),
                api.ticketTemplate.getTicketTemplateNames(organizationInformationId)
            ]);
            setService(serviceResp);
            ticketTemplateOptionsResponse &&
                setTicketTemplateOptions(
                    ticketTemplateOptionsResponse.map((x) => {
                        return {
                            key: x.key,
                            text: x.text ? x.text : ''
                        };
                    })
                );
        } catch (ex) {
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (values: IServiceListItem, formikActions: formik.FormikHelpers<IServiceListItem>) => {
        try {
            setIsLoading(true);
            formikActions.setStatus();
            await api.service.updateService(values);
            toast.warning(
                'Paslaugos informacija sėkmingai išsaugota, norint kad paslaugos pavadinimas ir/ar darbo laikas pasikeistų terminale, prašome jį perkrauti'
            );

            formikActions.resetForm();
            await refetchServices();
        } catch (ex) {
            formikActions.setStatus(ex);
            toast.error(ex.error[0]);
        } finally {
            formikActions.setSubmitting(false);
            setIsLoading(false);
            onClose();
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <>
            {isLoading ? (
                <KendoLoading />
            ) : (
                <>
                    {service && ticketTemplateOptions && (
                        <formik.Formik validationSchema={validationSchema} initialValues={{ ...service }} onSubmit={handleSubmit}>
                            {(service) => (
                                <formik.Form>
                                    <hr />
                                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                                        <div className="ms-Grid-col ms-sm4 ms-md4 ms-lg4">
                                            <formik.Field
                                                name="name_LT"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        label="Paslaugos pavadinimas LT"
                                                        {...field}
                                                        errorMessage={
                                                            (service.status && service.status.name_LT) || (form.errors.name_LT && form.errors.name_LT)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="ms-Grid-col ms-sm4 ms-md4 ms-lg4">
                                            <formik.Field
                                                name="name_EN"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        label="Paslaugos pavadinimas EN"
                                                        {...field}
                                                        errorMessage={
                                                            (service.status && service.status.name_EN) || (form.errors.name_EN && form.errors.name_EN)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="ms-Grid-col ms-sm4 ms-md4 ms-lg4">
                                            <formik.Field
                                                name="name_RU"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        label="Paslaugos pavadinimas RU"
                                                        {...field}
                                                        errorMessage={
                                                            (service.status && service.status.name_RU) || (form.errors.name_RU && form.errors.name_RU)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>

                                        <div className="ms-Grid-col ms-sm4 ms-md4 ms-lg4">
                                            <formik.Field
                                                name="description_LT"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        label="Paslaugos aprašymas LT"
                                                        multiline
                                                        {...field}
                                                        errorMessage={
                                                            (service.status && service.status.description_LT) ||
                                                            (form.errors.description_LT && form.errors.description_LT)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="ms-Grid-col ms-sm4 ms-md4 ms-lg4">
                                            <formik.Field
                                                name="description_EN"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        label="Paslaugos aprašymas EN"
                                                        multiline
                                                        {...field}
                                                        errorMessage={
                                                            (service.status && service.status.description_EN) ||
                                                            (form.errors.description_EN && form.errors.description_EN)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="ms-Grid-col ms-sm4 ms-md4 ms-lg4">
                                            <formik.Field
                                                name="description_RU"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        label="Paslaugos aprašymas RU"
                                                        multiline
                                                        {...field}
                                                        errorMessage={
                                                            (service.status && service.status.description_RU) ||
                                                            (form.errors.description_RU && form.errors.description_RU)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>

                                        <div className="ms-Grid-col ms-sm4 ms-md4 ms-lg4">
                                            <formik.Field
                                                name="startWorkingHours"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        defaultValue={moment(service.values.startWorkingHours).format('HH:mm')}
                                                        onChange={({}, value: any) => form.setFieldValue('startWorkingHours', value)}
                                                        label="Paslaugos darbo laiko pradžia"
                                                        type="time"
                                                        errorMessage={
                                                            (service.status && service.status.startWorkingHours) ||
                                                            (form.errors.startWorkingHours && form.errors.startWorkingHours)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="ms-Grid-col ms-sm4 ms-md4 ms-lg4">
                                            <formik.Field
                                                name="endWorkingHours"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        onChange={({}, value: any) => form.setFieldValue('endWorkingHours', value)}
                                                        defaultValue={moment(service.values.endWorkingHours).format('HH:mm')}
                                                        label="Paslaugos darbo laiko pabaiga"
                                                        type="time"
                                                        errorMessage={
                                                            (service.status && service.status.endWorkingHours) ||
                                                            (form.errors.endWorkingHours && form.errors.endWorkingHours)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="ms-Grid-col ms-sm4 ms-md4 ms-lg4">
                                            <formik.Field
                                                name="displayInTerminalType"
                                                render={({ field, form }: formik.FieldProps<number>) => (
                                                    <fabric.Dropdown
                                                        label="Paslaugos rodymas"
                                                        {...field}
                                                        selectedKey={field.value}
                                                        defaultSelectedKey={form.values.displayInTerminalType}
                                                        onChange={({}, item) => form.setFieldValue(field.name, item && item.key)}
                                                        errorMessage={
                                                            (service.status && service.status.displayInTerminalType) ||
                                                            (form.errors.displayInTerminalType && form.errors.displayInTerminalType)
                                                        }
                                                        options={servicesDisplayInTerminalType.displayTypesArray()}
                                                    />
                                                )}
                                            />
                                        </div>

                                        <div className="ms-Grid-col ms-sm3 ms-md3 ms-lg3" style={{ marginTop: '20px' }}>
                                            <formik.Field
                                                name="isAdditionalIdentificationRequired"
                                                render={({ field, form }: formik.FieldProps<boolean>) => (
                                                    <fabric.Checkbox
                                                        label="Ar reikalinga papildoma identifikacija?"
                                                        {...field}
                                                        value={`${field.value}`}
                                                        checked={field.value}
                                                    />
                                                )}
                                            />
                                        </div>

                                        <div className="ms-Grid-col ms-sm3 ms-md3 ms-lg3">
                                            <formik.Field
                                                name="servicePurposeType"
                                                render={({ field, form }: formik.FieldProps<number>) => (
                                                    <fabric.Dropdown
                                                        label="Paslaugos paskirties tipas"
                                                        {...field}
                                                        selectedKey={field.value}
                                                        defaultSelectedKey={form.values.servicePurposeType}
                                                        onChange={({}, item) => form.setFieldValue(field.name, item && item.key)}
                                                        errorMessage={
                                                            (service.status && service.status.servicePurposeType) ||
                                                            (form.errors.servicePurposeType && form.errors.servicePurposeType)
                                                        }
                                                        options={servicePurposeType.displayTypesArray()}
                                                    />
                                                )}
                                            />
                                        </div>

                                        <div className="ms-Grid-col ms-sm3 ms-md3 ms-lg3">
                                            <formik.Field
                                                name="ticketNumerationType"
                                                render={({ field, form }: formik.FieldProps<number>) => (
                                                    <fabric.Dropdown
                                                        label="Paslaugos numeracijos tipas"
                                                        {...field}
                                                        selectedKey={field.value}
                                                        defaultSelectedKey={form.values.ticketNumerationType}
                                                        onChange={({}, item) => form.setFieldValue(field.name, item && item.key)}
                                                        errorMessage={
                                                            (service.status && service.status.ticketNumerationType) ||
                                                            (form.errors.ticketNumerationType && form.errors.ticketNumerationType)
                                                        }
                                                        options={ticketsNumerationType.displayTypesArray()}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <fabric.Text variant="xLarge">Paslaugos bilieto spausdinimo nustatymai</fabric.Text>
                                    <hr />
                                    {service.values.ticketNumerationType === ticketsNumerationType.ServiceNumeration && (
                                        <>
                                            <div className="ms-Grid-row">
                                                <div className="ms-Grid-col ms-sm4 ms-md4 ms-lg4">
                                                    <formik.Field
                                                        name="ticketNumberPrefix"
                                                        render={({ field, form }: formik.FieldProps<string>) => (
                                                            <fabric.TextField
                                                                {...field}
                                                                label="Bilieto numerio priešdėlis"
                                                                placeholder="Pvz: AB"
                                                                maxLength={2}
                                                                suffix={(!field.value ? 'AB' : field.value) + '-001'}
                                                                errorMessage={
                                                                    (service.status && service.status.ticketNumberPrefix) ||
                                                                    (form.errors.ticketNumberPrefix && form.errors.ticketNumberPrefix)
                                                                }
                                                            />
                                                        )}
                                                    />
                                                </div>
                                                <div className="ms-Grid-col ms-sm4 ms-md4 ms-lg4">
                                                    <formik.Field
                                                        name="ticketNumberRangeFrom"
                                                        render={({ field, form }: formik.FieldProps<string>) => (
                                                            <fabric.TextField
                                                                {...field}
                                                                label="Bilieto numerio diapazono pradžia"
                                                                type="number"
                                                                maxLength={3}
                                                                errorMessage={
                                                                    (service.status && service.status.ticketNumberRangeFrom) ||
                                                                    (form.errors.ticketNumberRangeFrom && form.errors.ticketNumberRangeFrom)
                                                                }
                                                            />
                                                        )}
                                                    />
                                                </div>
                                                <div className="ms-Grid-col ms-sm4 ms-md4 ms-lg4">
                                                    <formik.Field
                                                        name="ticketNumberRangeTo"
                                                        render={({ field, form }: formik.FieldProps<string>) => (
                                                            <fabric.TextField
                                                                {...field}
                                                                label="Bilieto numerio diapazono pabaiga"
                                                                type="number"
                                                                maxLength={3}
                                                                errorMessage={
                                                                    (service.status && service.status.ticketNumberRangeTo) ||
                                                                    (form.errors.ticketNumberRangeTo && form.errors.ticketNumberRangeTo)
                                                                }
                                                            />
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    <div className="ms-Grid-row" dir="ltr">
                                        <div className="ms-Grid-col ms-sm3 ms-md3 ms-lg3">
                                            <formik.Field
                                                name="ticketTemplateId"
                                                render={({ field, form }: formik.FieldProps<number>) => (
                                                    <fabric.Dropdown
                                                        label="Bilieto šablono tipas"
                                                        selectedKey={field.value}
                                                        defaultSelectedKey={form.values.ticketTemplateId}
                                                        options={ticketTemplateOptions}
                                                        onChange={({}, item) => form.setFieldValue(field.name, item && item.key)}
                                                        errorMessage={
                                                            (service.status && service.status.ticketTemplateId) ||
                                                            (form.errors.ticketTemplateId && form.errors.ticketTemplateId)
                                                        }
                                                        styles={{ dropdown: { width: 300 } }}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'center', margin: '30px 0px' }}>
                                        <fabric.PrimaryButton
                                            className="ms-slideRightIn40 ms-textAlignRight"
                                            text="Išsaugoti"
                                            disabled={service.isSubmitting || !service.isValid || !service.dirty}
                                            type="submit"
                                        />
                                    </div>
                                </formik.Form>
                            )}
                        </formik.Formik>
                    )}
                </>
            )}
        </>
    );
};

export default ServiceEditModalWindow;
